import { SubmitErrorHandler, SubmitHandler, useForm } from 'react-hook-form'

import { useMutation } from '@tanstack/react-query'
import { getRouteApi } from '@tanstack/react-router'
import { routes } from 'commons/constants/routes'
import { Request } from 'commons/utils/request'
import { Toast } from 'ui/components'
import { handleFormSubmissionErrorRHF } from 'ui/components/Form'
import { SarEdit } from '~bank-bsa-reporting/types'
import { SarEditPayload } from '~bank-bsa-reporting/types/sar'

import { formToPayloadMapper } from '../helpers/payloadMapper'

const routeApi = getRouteApi('/_app/bsa/sars/$id/edit')

const request = (opts: SarEditPayload) => {
  const f = opts.id ? Request.put : Request.post
  return f(Request.urls.bank.sar, opts)
}

export const useSARForm = (sar: SarEdit) => {
  const { returnUrl } = routeApi.useSearch()
  const navigate = routeApi.useNavigate()

  const formApi = useForm({
    shouldFocusError: false,
    defaultValues: sar,
    mode: 'onBlur',
    reValidateMode: 'onChange'
  })

  const comeBackToSarOrReturnUrl = () => {
    if (returnUrl) {
      return navigate({ to: returnUrl as never })
    } else {
      if (sar?.id) {
        return navigate({ to: routes.bank.bsa.sar.details, params: { id: sar.id.toString() } })
      } else {
        return navigate({ to: routes.bank.bsa.sar.index })
      }
    }
  }

  const { mutate, isPending: isSubmitting } = useMutation<void, Error, SarEditPayload>({
    mutationFn: (opts) => request(opts),
    onError: (error) => {
      handleFormSubmissionErrorRHF<SarEdit>(error, formApi)
    },
    onSuccess: (_, payload) => {
      formApi.reset()
      setTimeout(() => {
        comeBackToSarOrReturnUrl()
      }, 0)
      Toast.successSmall(`SAR was successfully ${payload.id ? 'saved' : 'created'}`)
    }
  })

  const onSubmit: SubmitHandler<SarEdit> = (value) => {
    if (
      !(
        value.suspiciousActivityInfo.amountUnknown ||
        value.suspiciousActivityInfo.noAmountInvolved ||
        value.suspiciousActivityInfo.currentAmount
      )
    ) {
      const element = document.querySelector(`[data-qa="Item29"]`)
      element?.scrollIntoView({ block: 'center', behavior: 'smooth' })
      formApi.setError('suspiciousActivityInfo.currentAmount', {
        message:
          'Item 29: A dollar amount is required if neither the "Amount Unknown" nor ' +
          'the "No Amount Involved" options have been selected'
      })
    } else {
      const payload = formToPayloadMapper(value)
      mutate(payload)
    }
  }

  const onError: SubmitErrorHandler<SarEdit> = () => {
    const element = document.querySelector(`[data-field-with-error]`)
    element?.scrollIntoView({ block: 'center', behavior: 'smooth' })
  }

  return {
    isSubmitting,
    isFormDirty: formApi.formState.isDirty,
    formApi,
    handleSubmit: formApi.handleSubmit(onSubmit, onError)
  }
}
